import { NavItem } from './styled';
import { useTracking } from 'react-tracking';
import {
  ROUTE_COMMUNITY_IDEA,
  ROUTE_CREATE_CRITERIA,
  ROUTE_DASHBOARD,
  ROUTE_DASHBOARD_WITH_EVENTS,
  ROUTE_DEPRECATED_PORTFOLIO,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
  ROUTE_DEPRECATED_PORTFOLIO_HOME,
  ROUTE_DEPRECATED_PORTFOLIO_LIST,
  ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ROUTE_DEPRECATED_PORTFOLIO_SHARE,
  ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
  ROUTE_MY_PORTFOLIOS,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
  ROUTE_EDIT_EXPLORE_IDEA,
  ROUTE_EDIT_USER_IDEA,
  ROUTE_HOME,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_MARKET,
  ROUTE_MARKET_HOME,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_DEMO,
  ROUTE_PORTFOLIO_DEFAULT,
  ROUTE_PORTFOLIO_NEW,
  ROUTE_WATCHLIST,
  ROUTE_STOCKS,
  ROUTE_STOCKS_HOME,
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
  ROUTE_NARRATIVE_COMMUNITY,
} from '@/constants/routes';
import { useHomeRoute } from '@/hooks/useHomeRoute';
import { useTranslation } from '@/hooks/useTranslation';
import { getLocation } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import { Fragment } from 'react';
import { useFlag } from '@/features/unleash';

const subRouteTypes = {
  [ROUTE_HOME]: [ROUTE_DASHBOARD, ROUTE_DASHBOARD_WITH_EVENTS],
  [ROUTE_MARKET_HOME]: [
    ROUTE_MARKET,
    ROUTE_MARKET_HOME,
    ROUTE_MARKET_SECTOR,
    ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
    ROUTE_MARKET_SECTOR_SECONDARY,
  ],
  [ROUTE_DISCOVER_INVESTING_IDEAS]: [
    ROUTE_DISCOVER_INVESTING_IDEAS,
    ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
    ROUTE_INVESTING_IDEA_MARKET,
    ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
    ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
    ROUTE_COMMUNITY_IDEA,
    ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
    ROUTE_DEPRECATED_PORTFOLIO_SHARE,
    ROUTE_STOCKS,
    ROUTE_STOCKS_HOME,
    ROUTE_STOCKS_MARKET,
    ROUTE_STOCKS_MARKET_INDUSTRY,
  ],
  [ROUTE_WATCHLIST]: [ROUTE_WATCHLIST],
  [ROUTE_DEPRECATED_PORTFOLIO_LIST]: [
    ROUTE_PORTFOLIO,
    ROUTE_PORTFOLIO_DEMO,
    ROUTE_PORTFOLIO_NEW,
    ROUTE_PORTFOLIO_DEFAULT,
    ROUTE_DEPRECATED_PORTFOLIO_LIST,
    ROUTE_MY_PORTFOLIOS,
    ROUTE_DEPRECATED_PORTFOLIO,
    ROUTE_DEPRECATED_PORTFOLIO_EDIT,
    ROUTE_DEPRECATED_PORTFOLIO_HOME,
    ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
    ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ],
  [ROUTE_NARRATIVE_COMMUNITY]: [ROUTE_NARRATIVE_COMMUNITY],
  [ROUTE_CREATE_CRITERIA]: [
    ROUTE_CREATE_CRITERIA,
    ROUTE_EDIT_USER_IDEA,
    ROUTE_EDIT_EXPLORE_IDEA,
  ],
};

const useSelected = () => {
  const locationState = useSelector(getLocation);
  const isSelected = (currentRouteType: keyof typeof subRouteTypes) => {
    return subRouteTypes[currentRouteType]?.includes(locationState?.type);
  };
  return isSelected;
};

export function Nav() {
  const { trackEvent } = useTracking();
  const homeRoute = useHomeRoute();
  const isSelected = useSelected();
  const t = useTranslation();
  const { enabled, flagsReady } = useFlag({ name: 'mono-narrative-community' });
  const isNarrativeCommunityEnabled = enabled && flagsReady;

  const navItems = {
    dashboard: (
      <NavItem
        to={{
          type: homeRoute,
        }}
        inheritColor
        noUnderline
        selected={isSelected(ROUTE_HOME)}
        onClick={() => {
          !isSelected(ROUTE_HOME) &&
            trackEvent({
              modifier: 'Dashboard',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.dashboard')}</span>
      </NavItem>
    ),
    markets: (
      <NavItem
        to={{
          type: ROUTE_MARKET_HOME,
        }}
        inheritColor
        noUnderline
        selected={false}
        onClick={() => {
          !isSelected(ROUTE_MARKET_HOME) &&
            trackEvent({
              modifier: 'Markets',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.markets')}</span>
      </NavItem>
    ),
    discover: (
      <NavItem
        to={{
          type: ROUTE_DISCOVER_INVESTING_IDEAS,
        }}
        inheritColor
        noUnderline
        selected={
          isSelected(ROUTE_DISCOVER_INVESTING_IDEAS) ||
          isSelected(ROUTE_MARKET_HOME)
        }
        onClick={() => {
          !isSelected(ROUTE_DISCOVER_INVESTING_IDEAS) &&
            trackEvent({
              modifier: 'Discover',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.discover')}</span>
      </NavItem>
    ),
    watchlist: (
      <NavItem
        to={{
          type: ROUTE_WATCHLIST,
        }}
        inheritColor
        noUnderline
        selected={isSelected(ROUTE_WATCHLIST)}
        onClick={() => {
          !isSelected(ROUTE_WATCHLIST) &&
            trackEvent({
              modifier: 'Watchlist',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.watchlist')}</span>
      </NavItem>
    ),
    community: (
      <NavItem
        to={{
          type: ROUTE_NARRATIVE_COMMUNITY,
        }}
        inheritColor
        noUnderline
        selected={isSelected(ROUTE_NARRATIVE_COMMUNITY)}
        onClick={() => {
          !isSelected(ROUTE_NARRATIVE_COMMUNITY) &&
            trackEvent({
              modifier: 'Community',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.community')}</span>
      </NavItem>
    ),
    portfolios: (
      <NavItem
        to={{
          type: ROUTE_DEPRECATED_PORTFOLIO_LIST,
        }}
        inheritColor
        noUnderline
        selected={isSelected(ROUTE_DEPRECATED_PORTFOLIO_LIST)}
        onClick={() => {
          !isSelected(ROUTE_DEPRECATED_PORTFOLIO_LIST) &&
            trackEvent({
              modifier: 'Portfolios',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.portfolios')}</span>
      </NavItem>
    ),
    screener: (
      <NavItem
        to={{
          type: ROUTE_CREATE_CRITERIA,
        }}
        inheritColor
        noUnderline
        selected={isSelected(ROUTE_CREATE_CRITERIA)}
        onClick={() => {
          !isSelected(ROUTE_CREATE_CRITERIA) &&
            trackEvent({
              modifier: 'Screener',
              action: 'click',
            });
        }}
      >
        <span>{t('app.topbar.nav.screener')}</span>
      </NavItem>
    ),
  } as const;
  type NavItemKey = keyof typeof navItems;

  return (
    <>
      {(
        [
          'dashboard',
          'portfolios',
          'watchlist',
          isNarrativeCommunityEnabled && 'community',
          'discover',
          'screener',
          !isNarrativeCommunityEnabled && 'markets',
        ] as NavItemKey[]
      )
        .filter(Boolean)
        .map((key) => (
          <Fragment key={key}>{navItems[key]}</Fragment>
        ))}
    </>
  );
}
